import {AuthClientError, AuthClientEvent, AuthClientTokens} from '@react-keycloak/core';
import {ReactKeycloakProvider} from '@react-keycloak/web';
import Keycloak from 'keycloak-js';
import React, {useCallback, useEffect, useState} from 'react';

import {useUserActivity} from '../../../hooks/useUserActivity';
import {LOGIN_REDUCER_STATE_ID} from '../login_constants';
import {LIBRARY_NAME} from 'src/js/common/constants';
import {ConfigObjectType, StateId} from 'src/js/common/types';
import {LogUtils} from 'src/js/common/utils';
import {resetAccessToken, saveAccessToken} from 'src/js/common/utils/security_utils';
import {I18N_REDUCER_STATE_ID} from 'src/js/containers/i18n/i18n_constants';
import {logout, resetIdleTime} from 'src/js/containers/login/login_actions';
import {statusSlice} from 'src/js/containers/status/status_slice';
import {usePrevious} from 'src/js/hooks/usePrevious';
import {useAppDispatch, useAppSelector} from 'src/js/store_hooks';

const logger = LogUtils.getLogger('ReactKeycloakProviderWrapper', LIBRARY_NAME);

const KEYCLOAK_INIT_CONFIG = {
  onLoad: 'check-sso',
  pkceMethod: 'S256',
  silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
};
const DEFAULT_SESSION_IDLE_TIME = 30 * 60 * 1000; // 30 mins

interface OwnProps {
  config: ConfigObjectType;
  children: React.ReactNode;
  keycloak: Keycloak;
  stateId: StateId;
}
type Props = OwnProps;

const ReactKeycloakProviderWrapper = (props: Props) => {
  const {config, children, keycloak, stateId} = props;

  const currentLocale = useAppSelector((state) => state[I18N_REDUCER_STATE_ID].currentLocale);
  const idleTimeReset = useAppSelector((state) => state[stateId].idleTimeReset);
  const prevIdleTimeReset = usePrevious<boolean>(idleTimeReset);
  const dispatch = useAppDispatch();
  const [isInitialized, setIsInitialized] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  const [sessionIdleTime, setSessionIdleTime] = useState<number>(DEFAULT_SESSION_IDLE_TIME);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const raw = sp.get('sessionTimeout');
      if (raw != null) {
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed > 0) {
          setSessionIdleTime(parsed);
          logger.info(`Session idle timeout set to ${parsed} ms (source: URL param "sessionTimeout").`);
          return;
        }
        logger.warn(`Invalid sessionTimeout URL param "${raw}". Falling back to default ${DEFAULT_SESSION_IDLE_TIME} ms.`);
      }
    } catch {
      // ignore, fall back to default
    }
    logger.info(`Session idle timeout set to ${DEFAULT_SESSION_IDLE_TIME} ms (source: DEFAULT_SESSION_IDLE_TIME).`);
  }, []);

  /**
   * Reset keycloak.js tokens and login again.
   */
  const logoutUser = useCallback(() => {
    dispatch(logout(stateId, config.service.url.logout, keycloak));
  }, [config, dispatch, keycloak, stateId]);

  const runSessionIdleTimer = useCallback(() => {
    if (timer != null) {
      // clear previous timer
      clearTimeout(timer);
    }
    // create new timer with reset full idle time value
    const timerId = setTimeout(logoutUser, sessionIdleTime);
    setTimer(timerId);
  }, [timer, logoutUser, sessionIdleTime]);

  useEffect(() => {
    // we are not using old access tokens, always reset stored token on startup
    resetAccessToken();
  }, []);

  // check for timer reset and rerun it
  useEffect(() => {
    if (idleTimeReset && !prevIdleTimeReset) {
      // timer reset has occurred
      runSessionIdleTimer();
      dispatch(resetIdleTime(stateId, false));
    }
  }, [dispatch, idleTimeReset, prevIdleTimeReset, runSessionIdleTimer, stateId]);

  /**
   * Handles keycloak.js events 'onReady', 'onAuthSuccess', 'onAuthRefreshSuccess', 'onAuthLogout', 'onTokenExpired'
   * and keycloak.js error events 'onAuthError', 'onAuthRefreshError', 'onInitError'.
   * @param event
   * @param error
   */
  const handleEvent = (event: AuthClientEvent, error?: AuthClientError) => {
    // events 'onReady', 'onAuthSuccess', 'onAuthRefreshSuccess', 'onAuthLogout', 'onTokenExpired'
    // error events 'onAuthError', 'onAuthRefreshError', 'onInitError'
    if (error) {
      logger.error(event, error);
    } else if (event === 'onReady') {
      dispatch(statusSlice.actions.clearErrors());
      logger.info('Authorization Provider was successfully initialized.');
      setIsInitialized(true);
    } else if (event === 'onTokenExpired') {
      logger.info('Token has expired. Trying to refresh it.');
      keycloak.updateToken(5);
    }
  };

  /**
   * Handles keycloak.js change of tokens.
   * @param tokens
   */
  const handleTokens = (tokens: AuthClientTokens) => {
    if (tokens && tokens.token) {
      // we have a valid token, store it for authorized requests
      saveAccessToken(tokens);
    } else {
      resetAccessToken();
      // token are not available, login with a current user locale
      keycloak.login({locale: currentLocale});
    }
  };

  useUserActivity(
    () => {
      dispatch(resetIdleTime(LOGIN_REDUCER_STATE_ID, true));
    },
    {
      debounceMs: 5000,
      leading: true,
      maxWaitMs: 6000,
    }
  );

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={KEYCLOAK_INIT_CONFIG}
      onEvent={handleEvent}
      onTokens={handleTokens}
      LoadingComponent={<span data-name={'react-keycloak-provider-wrapper--loading'}>Loading ...</span>}
    >
      {isInitialized ? children : null}
    </ReactKeycloakProvider>
  );
};

ReactKeycloakProviderWrapper.displayName = 'ReactKeycloakProviderWrapper';

export default React.memo(ReactKeycloakProviderWrapper);
